import { Injectable, inject } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpContextToken,
} from "@angular/common/http";
import { from, Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { AuthService } from "../services/auth.service";
import { ToastrService } from "ngx-toastr";

export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/** Marks a call made by a signed-out visitor: no auth header, no toast, no forced logout. */
export const PUBLIC_REQUEST = new HttpContextToken<boolean>(() => false);

@Injectable()
export class Interceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const isPublic = request.context.get(PUBLIC_REQUEST);

    return from(isPublic ? Promise.resolve(null) : this.authService.getAuthToken()).pipe(
      switchMap((authToken: string | null) => {
        if (authToken) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${authToken}`,
            },
          });
        }
        return next.handle(request).pipe(
          catchError((error: HttpErrorResponse) => {
            const manuallyHandledAPIs = [
              "/api/outreach/",
              "/api/aitools/safety/",
              "/api/ai-email-writer/",
              "/api/crm/creators/import/",
              // Stats outage (502) degrades the Link Attribution tiles to "—"
              // in-component — a global toast on every load would be noise.
              "/api/attribution/creators/",
            ];
            const isManuallyHandled = manuallyHandledAPIs.some((path) =>
              error.url?.includes(path)
            );
            const isToastSuppressed = isPublic || request.context.get(SUPPRESS_ERROR_TOAST);

            if (!isManuallyHandled && !isToastSuppressed) {
              const errorMessage = this.extractErrorMessage(error);

              switch (error.status) {
                case 400:
                  this.toastr.error(
                    errorMessage ||
                      "Invalid request. Please check your input and try again."
                  );
                  break;
                case 401:
                  this.authService.logout();
                  this.toastr.error(
                    errorMessage ||
                      "Your session has expired. Please login again."
                  );
                  break;
                case 403:
                  if (!authToken) this.authService.logout();
                  this.toastr.error(
                    errorMessage ||
                      "You do not have permission to perform this action."
                  );
                  break;
                case 404:
                  this.toastr.error(
                    errorMessage || "The requested resource was not found."
                  );
                  break;
                case 422:
                  this.toastr.error(
                    errorMessage || "Validation error. Please check your input."
                  );
                  break;
                case 428:
                  // Precondition Required — handled manually in components
                  break;
                case 429:
                  this.toastr.error(
                    "Too many requests. Please slow down and try again."
                  );
                  break;
                case 500:
                  this.toastr.error(
                    errorMessage ||
                      "Server error occurred. Please try again later."
                  );
                  break;
                case 502:
                case 503:
                  this.toastr.error(
                    "Server is unavailable. Please try again later."
                  );
                  break;
                case 0:
                  this.toastr.error(
                    "Network error. Please check your connection and try again."
                  );
                  break;
                default:
                  this.toastr.error(
                    errorMessage || "Something went wrong, please try again."
                  );
                  break;
              }
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    if (error.error?.detail) return error.error.detail;
    if (error.error?.error?.message) return error.error.error.message;
    if (typeof error.error?.error === "string") return error.error.error;
    if (error.error?.message) return error.error.message;
    if (error.error && typeof error.error === "object" && !Array.isArray(error.error)) {
      const firstValue = Object.values(error.error)[0];
      if (typeof firstValue === "string") return firstValue;
      if (Array.isArray(firstValue) && typeof firstValue[0] === "string") return firstValue[0];
    }
    if (error.message) return error.message;
    return "";
  }
}
